import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Container, Card, Form, Button, Alert } from "react-bootstrap";
import jwtDecode from "jwt-decode";
import { PasswordInput } from "../../../components/PasswordInput";
import { useLoginUser } from "../hooks/useLoginUser";
import { useAuth } from "../../../app/providers/AuthProvider";

function Login() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  // Esta pantalla se sirve en dos rutas: `/:slug/login` (entrada por academia) y
  // `/login` (sin academia — es donde aterriza el staff de plataforma al cerrar
  // sesión, y donde redirigen los guards cuando no hay slug). Sin slug no se
  // pueden construir los enlaces tenant-scoped: se ocultan en vez de apuntar a
  // "/undefined/...".
  const hasSlug = Boolean(slug);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { mutate, isPending, isError, error } = useLoginUser();

  function handleSubmit(e) {
    e.preventDefault();
    mutate(
      { email, password },
      {
        onSuccess(data) {
          const { access } = data;

          let claims = {};
          try {
            claims = jwtDecode(access);
          } catch {
            /* malformed token — proceed with defaults */
          }

          const role = claims.role ?? null;
          // Use slug from JWT claim if present; fall back to URL param
          const tokenSlug = claims.slug ?? slug;

          // Refresh token is set as an HttpOnly cookie by the backend.
          login(access, {
            role,
            slug: tokenSlug,
            company_id: claims.company_id ?? null,
          });

          // El claim `slug` solo existe para miembros de una academia. El staff
          // de plataforma no pertenece a ninguna, así que sin este `if` se
          // navegaba literalmente a "/undefined/app/dashboard". Sin academia el
          // único destino con sentido es el panel; RequireSuperAdmin lo protege
          // y manda a /unauthorized a quien no sea staff.
          navigate(tokenSlug ? `/${tokenSlug}/app/dashboard` : "/superadmin", {
            replace: true,
          });
        },
      }
    );
  }

  const errorMsg = isError
    ? error?.response?.data?.detail ||
      error?.response?.data?.message ||
      "Credenciales incorrectas. Verifica tu correo y contraseña."
    : null;

  return (
    <Container
      className="d-flex justify-content-center align-items-center min-vh-100"
      style={{ padding: "1rem" }}
    >
      <Card className="w-100 shadow-sm" style={{ maxWidth: 440 }}>
        <Card.Body className="p-4">
          <h4 className="mb-1">Iniciar sesión</h4>
          <p className="text-muted mb-4">Bienvenido de vuelta.</p>

          {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

          <Form onSubmit={handleSubmit} noValidate>
            <Form.Group className="mb-3" controlId="loginEmail">
              <Form.Label>Correo electrónico</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                placeholder="correo@academia.com"
              />
            </Form.Group>

            <Form.Group className="mb-2" controlId="loginPassword">
              <Form.Label>Contraseña</Form.Label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="Tu contraseña"
              />
            </Form.Group>

            {hasSlug && (
              <div className="d-flex justify-content-end mb-4">
                <Link
                  to={`/${slug}/recuperar`}
                  className="text-muted"
                  style={{ fontSize: "0.875rem" }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-100"
              disabled={isPending}
            >
              {isPending ? "Entrando…" : "Iniciar sesión"}
            </Button>
          </Form>

          {hasSlug && (
            <p
              className="text-center mt-3 mb-0"
              style={{ fontSize: "0.875rem" }}
            >
              ¿Eres estudiante nuevo?{" "}
              <Link to={`/${slug}/registro`}>Regístrate aquí</Link>
            </p>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export { Login };
